import { FormControl, InputLabel, MenuItem, Select, Stack } from "@mui/material";
import { useState } from "react";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import type { ClientInfo } from "../../../api/clientUsersTypes";
import { api } from "../../../api/operationsApi";
import FieldHelp from "../../common/FieldHelp";
import LoadingFailedAlert from "../../common/LoadingFailedAlert";
import PageTitle from "../../common/PageTitle";
import RenameUserForm from "./RenameUserForm";

const RenameUserContent = () => {
  const [selectedOrganization, setSelectedOrganization] = useState<ClientInfo | null>(null);

  const [organizationsResponse, fetchOrganizationsError] = useFetch(api.clientUsers.getOrganizations);

  if (fetchOrganizationsError) {
    logError(fetchOrganizationsError, "[RenameUserContent] getOrganizations");
    return <LoadingFailedAlert apiError={fetchOrganizationsError} />;
  }

  if (organizationsResponse === undefined) {
    return <InlineLoader />;
  }

  const organizations = organizationsResponse.organizations;

  const handleOrganizationChange = (clientCode: string) => {
    setSelectedOrganization(organizations.find((o) => o.clientCode === clientCode) || null);
  };

  return (
    <Stack spacing={3} sx={{ alignItems: "flex-start", width: "28rem" }}>
      <PageTitle title="Rename User" subtitle="✏️ What this does" defaultExpanded>
        Update a client user&apos;s first name, last name, and email.
        <br />
        <br />
        • Updates the user in the Entrilia database (the name in every company of the organization, the email, and the
        sign-in email).
        <br />
        • Updates the same user in the client&apos;s Microsoft directory (display name, first/last name, email, and the
        #EXT# login name).
        <br />• Shows a link to the Business Central Users page. After this you must open Business Central and click
        &quot;Synchronize with Microsoft&quot;.
      </PageTitle>
      <Stack direction="row" sx={{ alignItems: "center", width: "100%" }}>
        <FormControl fullWidth>
          <InputLabel>Organization</InputLabel>
          <Select
            label="Organization"
            value={selectedOrganization?.clientCode || ""}
            onChange={(e) => handleOrganizationChange(e.target.value)}
          >
            {organizations.map((org) => (
              <MenuItem key={org.clientCode} value={org.clientCode}>
                {org.name} ({org.clientCode})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FieldHelp>Select the organization whose directory contains the user.</FieldHelp>
      </Stack>
      {selectedOrganization && (
        <RenameUserForm key={selectedOrganization.clientCode} organization={selectedOrganization} />
      )}
    </Stack>
  );
};

export default RenameUserContent;
